import { useThree } from "@react-three/fiber";
import { useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import { Raycaster, Vector2 } from "three";
import type { Object3D, Object3DEventMap } from "three";

const HOVER_THROTTLE_MS = 16;

type PointerHoverEventType = "pointerover" | "pointerout";
type HoverEventMap = Object3DEventMap & {
  pointerover: { type: "pointerover" };
  pointerout: { type: "pointerout" };
};

type HoverDispatchTarget = Object3D<HoverEventMap>;

const isHoverBlocked = (clientX: number, clientY: number): boolean => {
  const element = document.elementFromPoint(clientX, clientY);
  if (!(element instanceof Element)) {
    return false;
  }

  return Boolean(element.closest('[data-blocks-hover="true"]'));
};

const isRefAncestorOfObject = (
  refObject: Object3D | null,
  nearestObject: Object3D
): boolean => {
  let current: Object3D | null = nearestObject;
  while (current) {
    if (current === refObject) {
      return true;
    }

    current = current.parent;
  }

  return false;
};

const dispatchPointerEvent = (
  ref: RefObject<Object3D | null>,
  type: PointerHoverEventType
): void => {
  const target = ref.current as unknown as HoverDispatchTarget | null;
  target?.dispatchEvent({ type });
};

export function useHover<T extends Object3D>(
  ref: RefObject<T | null>,
  isEnabled = true
): boolean {
  const [hovered, setHovered] = useState(false);
  const { camera, gl, scene } = useThree();
  const raycasterRef = useRef(new Raycaster());
  const mouseRef = useRef(new Vector2());
  const lastCheckRef = useRef(0);
  // Use a ref to track current hover state to avoid stale closure issues.
  const hoveredRef = useRef(hovered);

  // Sync ref with state after render.
  useEffect(() => {
    hoveredRef.current = hovered;
  }, [hovered]);

  // Handle disabling hover - reset state when disabled.
  useEffect(() => {
    if (!isEnabled && hoveredRef.current) {
      setHovered(false);
      dispatchPointerEvent(ref, "pointerout");
    }
  }, [isEnabled, ref]);

  useEffect(() => {
    if (!(ref.current && isEnabled)) {
      return;
    }

    const canvas = gl.domElement;

    const setHoverState = (nextHovered: boolean) => {
      if (hoveredRef.current === nextHovered) {
        return;
      }

      setHovered(nextHovered);
      dispatchPointerEvent(ref, nextHovered ? "pointerover" : "pointerout");
    };

    const checkHover = (clientX: number, clientY: number) => {
      if (!(ref.current && isEnabled)) {
        return;
      }

      if (isHoverBlocked(clientX, clientY)) {
        setHoverState(false);
        return;
      }

      const rect = canvas.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((clientY - rect.top) / rect.height) * 2 + 1;

      mouseRef.current.set(x, y);
      raycasterRef.current.setFromCamera(mouseRef.current, camera);

      // Compute the nearest intersection across the whole scene.
      // Only treat this ref as hovered if the closest hit belongs to it.
      const hits = raycasterRef.current.intersectObjects(scene.children, true);
      const nearestHit = hits[0];
      const isIntersecting = nearestHit
        ? isRefAncestorOfObject(ref.current, nearestHit.object)
        : false;

      setHoverState(isIntersecting);
    };

    const throttledCheck = (clientX: number, clientY: number) => {
      const now = Date.now();
      if (now - lastCheckRef.current > HOVER_THROTTLE_MS) {
        // ~60fps throttling.
        lastCheckRef.current = now;
        checkHover(clientX, clientY);
      }
    };

    const handleMouseMove = (event: MouseEvent) => {
      throttledCheck(event.clientX, event.clientY);
    };

    const handleMouseLeave = () => {
      setHoverState(false);
    };

    // Use window-level listeners so HTML overlays do not block hover detection.
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [ref, isEnabled, camera, gl, scene]);

  return hovered;
}
